import { shortRoute } from '../route';
import { type AuditText, auditMessage } from '../text';
import type { AuditLogEntry } from '../types';

import { numberField, textField } from './lines';
import type { AuditSummary } from './types';

// Map lookups, so a reason such as "constructor" cannot reach Object.prototype.
const SSO_REASONS: ReadonlyMap<string, string> = new Map([
    ['provider_error', 'auditLog.summary.sso.providerError'],
    ['missing_code', 'auditLog.summary.sso.missingCode'],
    ['missing_verifier', 'auditLog.summary.sso.missingVerifier'],
    ['no_matching_user', 'auditLog.summary.sso.noMatchingUser'],
    ['error', 'auditLog.summary.sso.error'],
]);

function requestText(entry: AuditLogEntry): AuditText | null {
    const route = entry.route === null ? null : shortRoute(entry.route);
    if (entry.method !== null && route !== null) {
        return auditMessage('auditLog.summary.request', { method: entry.method, route });
    }
    return entry.method ?? route;
}

function exportReason(metadata: Record<string, unknown>, reason: string): AuditText | null {
    const matching = numberField(metadata, 'matching');
    // The backend stops counting at the limit plus one, so that is all `matching` says.
    if (reason === 'row_limit' && matching !== null) {
        return auditMessage('auditLog.summary.exportRefused', { limit: matching - 1 });
    }
    const rows = numberField(metadata, 'rows');
    if (rows === null) return null;
    if (reason === 'client_disconnected') {
        return auditMessage('auditLog.summary.exportInterrupted', { count: rows });
    }
    return reason === 'error'
        ? auditMessage('auditLog.summary.exportStopped', { count: rows })
        : null;
}

function knownReason(entry: AuditLogEntry): AuditText | null {
    const reason = entry.metadata === null ? null : textField(entry.metadata, 'reason');
    if (entry.metadata === null || reason === null) return null;
    if (entry.action === 'auth.login') {
        const key = SSO_REASONS.get(reason);
        return key === undefined ? null : auditMessage(key);
    }
    return entry.action === 'audit_log.export' ? exportReason(entry.metadata, reason) : null;
}

/** Rule 1: a denied or failed entry, by its recorded reason or else by its request. */
export function summarizeFailure(entry: AuditLogEntry): AuditSummary | null {
    if (entry.outcome === 'success') return null;
    const reason = knownReason(entry);
    const request = requestText(entry);
    const line = reason ?? request;
    if (line === null) return null;
    const title = reason !== null && request !== null ? [reason, request] : [line];
    return { rule: 'failure', line, title };
}
